import { lazy, Suspense } from 'react'
import { CircularProgress, Stack } from '@mui/material'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { AppShell } from './layout/AppShell'
import { useAuth } from './auth/AuthProvider'
import { isManagementConsole } from './lib/console'
import { LoginPage } from './pages/LoginPage'

const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })))
const SignupPage = lazy(() => import('./pages/SignupPage').then((m) => ({ default: m.SignupPage })))
const AdminDashboard = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.AdminDashboard })))
const TenantDashboard = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.TenantDashboard })))
const DomainsPage = lazy(() => import('./pages/DomainsPage').then((m) => ({ default: m.DomainsPage })))
const MailboxesPage = lazy(() => import('./pages/MailboxesPage').then((m) => ({ default: m.MailboxesPage })))
const AliasesPage = lazy(() => import('./pages/AliasesPage').then((m) => ({ default: m.AliasesPage })))
const MessagesPage = lazy(() => import('./pages/MessagesPage').then((m) => ({ default: m.MessagesPage })))
const InboxPage = lazy(() => import('./pages/InboxPage').then((m) => ({ default: m.InboxPage })))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage').then((m) => ({ default: m.CampaignsPage })))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage').then((m) => ({ default: m.TemplatesPage })))
const TenantsPage = lazy(() => import('./pages/TenantsPage').then((m) => ({ default: m.TenantsPage })))
const PlansPage = lazy(() => import('./pages/PlansPage').then((m) => ({ default: m.PlansPage })))
const ProfilesPage = lazy(() => import('./pages/ProfilesPage').then((m) => ({ default: m.ProfilesPage })))
const ObservabilityPage = lazy(() => import('./pages/ObservabilityPage').then((m) => ({ default: m.ObservabilityPage })))
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })))
const PlatformAdminsPage = lazy(() => import('./pages/PlatformAdminsPage').then((m) => ({ default: m.PlatformAdminsPage })))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })))
const PlatformSettingsPage = lazy(() => import('./pages/PlatformSettingsPage').then((m) => ({ default: m.PlatformSettingsPage })))
const OrganizationSettingsPage = lazy(() => import('./pages/OrganizationSettingsPage').then((m) => ({ default: m.OrganizationSettingsPage })))
const AuditLogPage = lazy(() => import('./pages/AuditLogPage').then((m) => ({ default: m.AuditLogPage })))
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage').then((m) => ({ default: m.ActivityLogPage })))
const TrashPage = lazy(() => import('./pages/TrashPage').then((m) => ({ default: m.TrashPage })))
const BackupsPage = lazy(() => import('./pages/BackupsPage').then((m) => ({ default: m.BackupsPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))

const fallback = <Stack minHeight={300} alignItems="center" justifyContent="center"><CircularProgress size={30} /></Stack>

// `/` decide a área inicial pelo papel: admin da plataforma cai na Plataforma, o resto na
// Organização.
function HomeRedirect() {
  const { isAdmin, isAdminReadOnly } = useAuth()
  const target = (isAdmin || isAdminReadOnly) && isManagementConsole() ? '/plataforma/visao-geral' : '/organizacao/visao-geral'
  return <Navigate to={target} replace />
}

// Redireciona as rotas antigas (pré-separação Plataforma/Organização) preservando a query.
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

const legacyRoutes: Record<string, string> = {
  '/domains': '/organizacao/dominios',
  '/mailboxes': '/organizacao/caixas-postais',
  '/aliases': '/organizacao/aliases',
  '/messages': '/organizacao/mensagens',
  '/inbox': '/organizacao/caixa-de-entrada',
  '/campaigns': '/organizacao/campanhas',
  '/templates': '/organizacao/modelos',
  '/activity': '/organizacao/atividade',
  '/users': '/organizacao/usuarios',
  '/api-keys': '/organizacao/api-keys',
  '/tenants': '/organizacao/organizacoes',
  '/plans': '/plataforma/planos',
  '/activity-profiles': '/plataforma/perfis-atividade',
  '/platform-admins': '/plataforma/administradores',
  '/audit-log': '/plataforma/atividade',
  '/trash': '/plataforma/lixeira',
  '/settings': '/plataforma/configuracoes',
}

export default function App() {
  return (
    <Suspense fallback={fallback}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
        <Route path="/contratar" element={<SignupPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<HomeRedirect />} />

          <Route path="/plataforma" element={<AppShell area="platform" />}>
            <Route index element={<Navigate to="visao-geral" replace />} />
            <Route path="visao-geral" element={<AdminDashboard />} />
            <Route path="organizacoes" element={<TenantsPage />} />
            <Route path="planos" element={<PlansPage />} />
            <Route path="perfis-atividade" element={<ProfilesPage />} />
            <Route path="metricas" element={<ObservabilityPage />} />
            <Route path="administradores" element={<PlatformAdminsPage />} />
            <Route path="atividade" element={<AuditLogPage />} />
            <Route path="lixeira" element={<TrashPage scope="platform" />} />
            <Route path="configuracoes" element={<PlatformSettingsPage />} />
          </Route>

          <Route path="/organizacao" element={<AppShell area="organization" />}>
            <Route index element={<Navigate to="visao-geral" replace />} />
            <Route path="visao-geral" element={<TenantDashboard />} />
            <Route path="organizacoes" element={<TenantsPage />} />
            <Route path="dominios" element={<DomainsPage />} />
            <Route path="caixas-postais" element={<MailboxesPage />} />
            <Route path="aliases" element={<AliasesPage />} />
            <Route path="usuarios" element={<UsersPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="atividade" element={<ActivityLogPage />} />
            <Route path="backups" element={<BackupsPage />} />
            <Route path="lixeira" element={<TrashPage scope="tenant" />} />
            <Route path="configuracoes" element={<OrganizationSettingsPage />} />
            <Route path="caixa-de-entrada" element={<InboxPage />} />
            <Route path="mensagens" element={<MessagesPage />} />
            <Route path="campanhas" element={<CampaignsPage />} />
            <Route path="modelos" element={<TemplatesPage />} />
          </Route>

          {Object.entries(legacyRoutes).map(([from, to]) => (
            <Route key={from} path={from} element={<LegacyRedirect to={to} />} />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  )
}
